// HomeHealthDashboard — hero = Next visit (patient-first) + AI care advisor.
// Uses /home-health/clients (returns { clients, counts }).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Heart, Sparkle, MapPin, Clock, ArrowRight, CheckCircle, Warning,
  House, PaperPlaneTilt, Pulse, Camera,
} from "@phosphor-icons/react";

const VISIT_CHIP = {
  overdue:   { cls: "bg-red-100 text-red-800",         label: "OVERDUE" },
  today:     { cls: "bg-fuchsia-100 text-fuchsia-800", label: "TODAY" },
  this_week: { cls: "bg-amber-100 text-amber-800",     label: "THIS WEEK" },
  future:    { cls: "bg-slate-100 text-slate-700",     label: "SCHEDULED" },
  unset:     { cls: "bg-slate-100 text-slate-500",     label: "UNSET" },
};

export default function HomeHealthDashboard() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/home-health/clients");
        setClients(r.data?.clients || []);
        setCounts(r.data?.counts || {});
      } catch { toast.error("Could not load clients"); }
      finally { setLoading(false); }
    })();
  }, []);

  // Sort order in backend puts overdue/today first
  const nextVisit = clients.find(c => ["overdue","today"].includes(c.visit_status)) || clients[0];
  const upcoming  = clients.filter(c => c.id !== nextVisit?.id).slice(0, 4);
  const anyFlags  = clients.filter(c => (c.latest_vitals?.flags || []).length > 0).length;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="hh-dashboard">
        {/* HERO — Next patient */}
        <div
          onClick={() => nav("/admin/home-health/clients")}
          data-testid="hh-hero-next"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-fuchsia-900 to-rose-900 p-6 sm:p-8 text-white hover:shadow-xl transition group"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><Heart weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-200 inline-flex items-center gap-1.5">
              <Heart weight="fill" size={11}/> HIPAA · Family portal · AI care
            </div>
            {nextVisit ? (
              <>
                <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="hh-hero-title">
                  Next visit:<br/>
                  <span className="text-fuchsia-200">{nextVisit.client_name}</span>
                </h1>
                <div className="text-sm text-fuchsia-100/80 mt-2 max-w-md inline-flex items-center gap-1.5">
                  <VisitChip s={nextVisit.visit_status}/>
                  <span>· {nextVisit.address || "Home visit"}</span>
                </div>
              </>
            ) : (
              <>
                <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="hh-hero-title">
                  No clients yet.<br/>
                  <span className="text-fuchsia-200">Add your first patient.</span>
                </h1>
              </>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-fuchsia-400 text-slate-900 font-black uppercase text-[11px] tracking-widest group-hover:scale-105 transition">
                <Sparkle weight="fill" size={13}/> Open Client Care Radar
              </div>
              {nextVisit?.address && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(nextVisit.address)}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="hh-hero-nav"
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest">
                  <MapPin weight="bold" size={13}/> Navigate
                </a>
              )}
            </div>
          </div>
        </div>

        {/* LATEST VITALS + FLAGS STRIP */}
        {nextVisit?.latest_vitals && (
          <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="hh-vitals">
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
                <Pulse size={12}/> Latest vitals · {nextVisit.client_name}
              </div>
              {(nextVisit.latest_vitals.flags || []).length > 0 && (
                <span className="text-[9px] font-black uppercase tracking-widest bg-red-100 text-red-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                  <Warning weight="fill" size={9}/> {nextVisit.latest_vitals.flags.length} flag{nextVisit.latest_vitals.flags.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="p-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
              <VitalTile label="BP" value={nextVisit.latest_vitals.systolic ? `${nextVisit.latest_vitals.systolic}/${nextVisit.latest_vitals.diastolic}` : "—"}/>
              <VitalTile label="Pulse" value={nextVisit.latest_vitals.pulse ?? "—"}/>
              <VitalTile label="O₂" value={nextVisit.latest_vitals.o2_sat ? `${nextVisit.latest_vitals.o2_sat}%` : "—"}/>
              <VitalTile label="Temp" value={nextVisit.latest_vitals.temp_f ? `${nextVisit.latest_vitals.temp_f}°` : "—"}/>
              <VitalTile label="Glucose" value={nextVisit.latest_vitals.glucose ?? "—"}/>
            </div>
          </div>
        )}

        {/* UPCOMING VISITS */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="hh-upcoming">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> Upcoming visits
            </div>
            <button type="button" onClick={() => nav("/admin/home-health/clients")}
              data-testid="hh-all-clients"
              className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 hover:text-fuchsia-800 inline-flex items-center gap-1">
              All clients <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="hh-no-upcoming">
              No more visits scheduled this week.
            </div>
          ) : (
            upcoming.map((c, i) => (
              <div key={c.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`hh-client-${c.id}`}>
                <div className="h-9 w-9 rounded-lg bg-fuchsia-50 text-fuchsia-600 flex items-center justify-center shrink-0">
                  <Heart weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{c.client_name}</div>
                  <div className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1">
                    <House size={10}/> {c.address || "—"} · {c.care_level || ""}
                  </div>
                </div>
                <VisitChip s={c.visit_status}/>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hh-funnel">
          <FunnelTile label="Overdue" value={counts.overdue || 0} icon={<Warning size={14}/>} accent="red"/>
          <FunnelTile label="Today" value={counts.today || 0} icon={<PaperPlaneTilt size={14}/>} accent="fuchsia"/>
          <FunnelTile label="This week" value={counts.this_week || 0} icon={<Clock size={14}/>} accent="amber"/>
          <FunnelTile label="Vitals flags" value={anyFlags} icon={<Warning size={14}/>} accent="red"/>
        </div>
      </div>
    </AdminShell>
  );
}

function VitalTile({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display font-black text-lg text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function VisitChip({ s }) {
  const m = VISIT_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    red:     "bg-red-50 border-red-200 text-red-900",
    fuchsia: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    slate:   "bg-slate-50 border-slate-200 text-slate-800",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
